/**
 * FishInventorySection — Fish tiles grouped by species with click-to-expand detail
 */

import { useMemo, useState } from 'react';
import { useGameStore, useInventory } from '@/state';
import { Quality, FishStack } from '@/types';
import { getFishDefinition } from '@/data';
import { ItemTile } from './ItemTile';
import { FishDetailPanel } from './FishDetailPanel';
import { fishStackToTileData } from './adapters';
import { EmptyState } from '@/components/layout/EmptyState';

interface FishInventorySectionProps {
  onNavigateToMarket: () => void;
}

interface SpeciesGroup {
  definitionId: string;
  name: string;
  emoji: string;
  stacks: FishStack[];
}

export function FishInventorySection({ onNavigateToMarket }: FishInventorySectionProps) {
  const inventory = useInventory();
  const protectedFish = useGameStore(
    (state) => state.playerState.autoSellSettings.protectedFish,
  );
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const getStackKey = (definitionId: string, quality: string) =>
    `${definitionId}:${quality}`;

  const handleTileClick = (definitionId: string, quality: string) => {
    const key = getStackKey(definitionId, quality);
    setSelectedKey(selectedKey === key ? null : key);
  };

  const selectedStack = selectedKey
    ? inventory.stacks.find(
        (s) => getStackKey(s.definitionId, s.quality as string) === selectedKey,
      )
    : null;

  // Group stacks by species
  const speciesGroups = useMemo(() => {
    const groupMap = new Map<string, SpeciesGroup>();

    for (const stack of inventory.stacks) {
      let group = groupMap.get(stack.definitionId);
      if (!group) {
        const def = getFishDefinition(stack.definitionId);
        group = {
          definitionId: stack.definitionId,
          name: def.name,
          emoji: def.emoji || '🐟',
          stacks: [],
        };
        groupMap.set(stack.definitionId, group);
      }
      group.stacks.push(stack);
    }

    return Array.from(groupMap.values());
  }, [inventory.stacks]);

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-bold text-white">🐟 Fish Inventory</h2>
          <p className="text-xs text-gray-400">
            {inventory.currentCount} / {inventory.capacity} slots used
          </p>
        </div>
        {inventory.stacks.length > 0 && (
          <button
            type="button"
            onClick={onNavigateToMarket}
            className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white px-4 py-2 rounded-lg font-bold text-sm transition-all shadow-lg flex items-center gap-1.5"
          >
            <span>💰</span>
            <span>Go to Market</span>
          </button>
        )}
      </div>

      {/* Fish grouped by species */}
      {speciesGroups.length === 0 ? (
        <EmptyState emoji="🎣" message="No fish yet. Go fishing!" />
      ) : (
        <div className="space-y-4">
          {speciesGroups.map((group) => (
            <div key={group.definitionId}>
              {/* Species header */}
              <div className="flex items-center gap-2 mb-1.5">
                <span className="text-base">{group.emoji}</span>
                <span className="text-sm font-semibold text-gray-300">{group.name}</span>
                <span className="text-xs text-gray-500">
                  ({group.stacks.reduce((sum, s) => sum + s.count, 0)} total)
                </span>
              </div>

              {/* Quality tiles for this species */}
              <div className="grid grid-cols-[repeat(auto-fill,minmax(160px,1fr))] gap-2">
                {group.stacks.map((stack) => {
                  const key = getStackKey(stack.definitionId, stack.quality as string);
                  const isProtected =
                    protectedFish[stack.definitionId]?.[stack.quality as Quality] === true;
                  const tileData = fishStackToTileData(stack, isProtected);

                  return (
                    <ItemTile
                      key={key}
                      data={tileData}
                      isSelected={selectedKey === key}
                      onClick={() => handleTileClick(stack.definitionId, stack.quality as string)}
                    />
                  );
                })}

                {/* Inline detail panel — spans full grid width */}
                {selectedStack && selectedStack.definitionId === group.definitionId && (
                  <FishDetailPanel
                    stack={selectedStack}
                    onClose={() => setSelectedKey(null)}
                    onNavigateToMarket={onNavigateToMarket}
                  />
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
